<template>
    <div
            data-test-id="builder-app"
            role="dialog"
            aria-modal="true"
            ref="tiles"
            tabindex="0"
            class="fixed inset-0 bg-white z-40 flex flex-col safe"
            :data-loading="!!loading"
    >
        <Loader
                data-test-id="builder-loader"
                overlay
                :visible="!!loading"
                class="absolute inset-0 text-blue-500 z-50"
                :label="translateString(loadingMessage)"
        />

        <Dialog
                data-close-confirm
                has-cancel-option
                :visible="showConfirmQuit"
                :cancel-label="$t('message.builder.dialogLabel.quit.cancelLabel')"
                :confirm-label="$t('message.builder.dialogLabel.quit.confirmLabel')"
                @confirm="handleClose"
                @cancel="showConfirmQuit = false"
        >
            <p tabindex="0">{{ $t('message.builder.quit') }}</p>
        </Dialog>

        <Dialog
                data-error
                :visible="!!error"
                type="negative"
                :confirm-label="$t('message.builder.dialogLabel.confirmLabel')"
                @confirm="handleCloseMessages"
        >
            <p>{{ translateString('message.constants.errors.', error) }}</p>
        </Dialog>

        <Dialog
                data-warning
                :visible="!!warning"
                type="alert"
                :confirm-label="$t('message.builder.dialogLabel.confirmLabel')"
                @confirm="handleCloseMessages"
        >
            <p>{{ translateString('message.constants.warnings.', warning) }}</p>
        </Dialog>

        <Dialog
                data-invalid
                :visible="!!invalid"
                type="negative"
                :confirm-label="$t('message.builder.dialogLabel.confirmLabel')"
                @confirm="handleClose"
        >
            <p>{{ translateString('message.constants.errors.', invalid) }}</p>
        </Dialog>
        <div v-if="selectedEditTileIndex !== null"
             class="fixed flex flex-col md:flex-row inset-0 top-0 left-0 bg-gray-50 z-30">
            <TileBuilder :tiles="tiles"
                         :selectedEditTileIndex="selectedEditTileIndex"
                         :showImageSelector="showImageSelector"
                         :key="`tileBuilder-${selectedEditTileIndex}-${tiles[selectedEditTileIndex].productData.size}`"
                         @handlePaneClick="handlePaneClick"
                         @saveChanges="saveTileChanges"></TileBuilder>
        </div>
        <Header class="border-b z-20" v-bind:showChat="showChat">
            <p class="absolute top-50 left-50 -translate-center text-sm">
                <template v-if="loading">
                    <div
                            data-test-id="total-placeholder"
                            class="w-16 h-4 bg-gray-100"
                    />
                </template>
                <template v-else>
                    <span>{{ $t('message.builder.total') }}: </span>
                    <span
                            v-if="hasDiscount"
                            data-discount-total
                            aria-hidden="true"
                            class="line-through text-gray-500 mr-1"
                    >{{ $filters.currency(totalPrice) }}</span>
                    <span
                            data-test-id="total"
                            data-total
                            class="text-blue-500 font-bold"
                    >{{ $filters.currency(totalPrice * (hasDiscount ? discountFactor : 1)) }}</span>
                </template>
            </p>
        </Header>

        <div class="flex-grow flex overflow-hidden relative bg-gray-50 flex-row z-10 overflow-x-auto"
             :class="tileContainerClassList"
             :style="isPetPortraitProduct ? 'height: calc(100% - 55px)' : ''">
            <div v-if="selectedEditTileIndex === null"
                 ref="tileContainer"
                 class="flex flex-grow-0 flex-shrink-0 overflow-x-auto z-10">
                <Tile v-for="(product, index) in tiles"
                      :key="`tile-${index}`"
                      ref="tile"
                      :product="product"
                      :images="product.productData.images"
                      :featureFlags="featureFlags"
                      :renderMode="renderMode"
                      :uploadLater="product.productData.uploadLater"
                      :canEdit="selectedEditTileIndex === index"
                      :isMobile="isMobile"
                      :showPreview="showPreview"
                      :disableCollageOuterBorder="true"
                      :isArt="false"
                      :selectedEditToolId="null"
                      :imageEditTrigger="null"
                      :locale="locale"
                      :wrapperPaddingClasslist="getWrapperPaddingClasslist(product.productData)"
                      :tileIndex="index"
                      @paneClick="handlePaneClick"></Tile>
            </div>
        </div>
        <div class="p-4 flex justify-center">
            <ActionButton
                    data-test-id="add-to-cart"
                    class="btn btn--primary w-full md:w-auto md:text-base md:px-32 md:py-4"
                    data-add-to-cart
                    :aria-label="''"
                    :label="addToCartLabel"
                    :disabled="!canAddToCart"
                    @click="handleAddTilesToCart"
            />
        </div>
        <IconButton
                data-close-standalone
                icon="x"
                ref="closeBtn"
                :size="'medium'"
                :accessibility-label="$t('message.builder.header.closeAccessibility')"
                :position="'absolute'"
                class="h-14 top-0 right-0 z-20"
                @click="handleShowConfirmQuit"
        />
        <transition-group name="transition-fade-in">
            <ImageSelector
                    key="1"
                    data-image-selector
                    v-if="showImageSelector"
                    :upload-later="supportsUploadLater"
                    :feature-flags="featureFlags"
                    @uploadLater="handleUploadLater"
                    @close="showImageSelector = false"
                    @confirm="handleSelectImage"
            />

            <div v-if="showImageSelector"
                 key="2"
                 @click="showImageSelector = false"
                 class="bg-black bg-opacity-75 absolute inset-0 z-30">
            </div>
        </transition-group>
    </div>
</template>

<script>
import * as dimensions from '@vuedir/constants/dimensions'
import * as routes from '@vuedir/constants/routes'
import { mapGetters, mapActions } from 'vuex'
import clickOffDirective, { LOADING_PROCESSING_IMAGE } from '@vuedir/directives/ClickOff'
import { trapTabFocus, translate, sanitizeSvg, serializeSvg, products } from '@canvaspop/scoops'
import { locale } from '@vuedir/plugins/i18n/i18n'

import ActionButton from '@vuedir/components/ActionButton'
import TileBuilder from '@vuedir/components/TileBuilder'
import Body from '@vuedir/components/Body'
import Dialog from '@vuedir/components/Dialog'
import Header from '@vuedir/components/Header'
import ImageSelector from '@vuedir/components/ImageSelector'
import Loader from '@vuedir/components/Loader'
import ImageEditor from '@vuedir/components/ImageEditor'
import Tile from '@vuedir/components/Tile'
import IconButton from '@vuedir/components/IconButton'
import {
    getSizePrice,
    getEdgePrice,
    getFramePrice,
    getFilterPrice,
    getServicePrice,
    getTotalPrice,
    getEdge,
    getFrame,
    getFilter,
    getService,
} from '@vuedir/utils/builder-utils'

/**
 * @const {HTMLBodyElement}
 */
const body = document.querySelector('body')

/**
 * Tiles.vue
 */
export default {
    directives: {
        'click-off': clickOffDirective,
    },
    components: {
        ActionButton,
        Body,
        Dialog,
        Header,
        ImageSelector,
        Loader,
        ImageEditor,
        Tile,
        IconButton,
        TileBuilder
    },

    data () {
        return {
            paneId: 'pane_1',
            tileIndex: null,
            customSize: false,

            showImageSelector: false,
            showFlyout: null,
            showPreview: false,
            showConfirmQuit: false,
            showChat: false,
            isMobile: true,
            showFlyoutDesktop: 0,
            renderMode: false,
            PRODUCT_ART_SINGLE_PANEL: products.PRODUCT_ART_SINGLE_PANEL,
            PRODUCT_ART_FRAMED_PRINT: products.PRODUCT_ART_FRAMED_PRINT,
            PRODUCT_COLLAGE_FRAMED_PRINT: products.PRODUCT_COLLAGE_FRAMED_PRINT,
            userManuallySelectedCustom: false,
            locale: locale,
            triggerElement: null,
            triggerTileElementId: null,
            selectedEditTileIndex: null,
            tileBuilderOpen: false,
            bundleInstanceId: null,
            tileContainerClassList: ''
        }
    },
    emits: ['close', 'chat'],
    computed: {
        ...mapGetters({
            featureFlags: 'app/featureFlags',

            loading: 'builder/loading',
            loadingMessage: 'builder/loadingMessage',
            error: 'builder/error',
            warning: 'builder/warning',
            invalid: 'builder/invalid',
            discountFactor: 'builder/discountFactor',
            couponSizes: 'builder/couponSizes',
            tiles: 'builder/tiles',

            productSlug: 'product/productSlug',
            bundleSlug: 'product/bundleSlug',
        }),


        hasDiscount () {
            return this.discountFactor < 1 && (!this.couponSizes.length || this.couponSizes.indexOf(this.selectedSize) !== -1)
        },

        supportsUploadLater () {
            let uploadLater = false

            if (this.productSlug) {
                if (this.productSlug === products.PRODUCT_PET_PORTRAIT && this.enableOffline) {
                    uploadLater = true
                } else {
                    uploadLater = products.SUPPORTS[this.productSlug].uploadLater
                }
            }

            return uploadLater
        },

        trapFocusEnabled () {
            return !this.showConfirmQuit && !this.error && !this.warning && !this.invalid && !this.showImageSelector
        },

        isPetPortraitProduct () {
            return this.productSlug === products.PRODUCT_PET_PORTRAIT
        },

        totalPrice () {
            let totals = []
            // get the sum of all the tile totals
            for (let i = 0; i < this.tiles.length; i ++) {
                let sizePrice = getSizePrice(this.tiles[i].sizes, this.tiles[i].productData.size)
                let edgePrice = getEdgePrice(this.tiles[i].sizes, this.tiles[i].productData.size, this.tiles[i].edges, getEdge(this.tiles[i].productData.options))
                let framePrice = getFramePrice(this.tiles[i].sizes, this.tiles[i].productData.size, this.tiles[i].frames, getFrame(this.tiles[i].productData.options))
                let filterPrice = getFilterPrice(this.tiles[i].sizes, this.tiles[i].filters, getFilter(this.tiles[i].productData.options))
                let servicePrice = getServicePrice(this.tiles[i].sizes, this.tiles[i].services, getService(this.tiles[i].productData.options))
                totals.push(getTotalPrice(sizePrice, edgePrice, framePrice, filterPrice, servicePrice))
            }
            return totals.reduce((accumulator, value) => {
                return accumulator + value;
            }, 0)
        },
        addToCartLabel () {
            return this.isModifying() ? this.$t('message.builder.addToCartLabel.existing') : this.$t('message.builder.addToCartLabel.new')
        },

        canAddToCart () {

            let panes = [
                {
                    'top': 0,
                    'left': 0,
                    'width': 100,
                    'height': 100,
                    'id': 'pane_1',
                },
            ]

            for (let i=0; i < this.tiles.length; i++) {
                const layouts = this.tiles[i].layouts
                const layout = this.tiles[i].productData.layout

                if (layouts && layout) {
                    panes = layouts[layout]?.panes || panes
                }
                const panesIds = panes.map(pane => pane.id)

                if(!this.tiles[i].productData.images){
                    this.tiles[i].productData.images = {}
                }

                const imageKeys = Object.keys(this.tiles[i].productData.images).filter(v => panesIds.includes(v))

                let isValid = (this.tiles[i].productData.uploadLater || (imageKeys.length === panesIds.length))
                if (!isValid) {

                    return false
                }
            }

            return true
        },
    },

    mounted () {
        this.addClasses()
        this.init()
        this.focusTiles()
        document.addEventListener('keydown', this.handleKeyDown)
    },

    unmounted() {
        this.focusTriggerElement()
        document.removeEventListener('keydown', this.handleKeyDown)
    },

    beforeUnmount () {
        this.removeClasses()
        window.removeEventListener('resize', this.evaluateViewport)
        this.disableKeyEscape()
    },

    methods: {
        ...mapActions({
            getTiles: 'builder/getTiles',
            getBundleInstanceTiles: 'builder/getBundleInstanceTiles',
            setTileImage: 'builder/setTileImage',
            cartAddTiles: 'builder/cartAddTiles',
            cartUpdateTiles: 'builder/cartUpdateTiles',
            unsetTileImage: 'builder/unsetTileImage',
            resetMessages: 'builder/resetMessages',
            setBundleSlug: 'product/setBundleSlug'
        }),
        aspectRatio (width, height) {
            if (width > height) {
                return dimensions.LANDSCAPE
            } else if (width < height) {
                return dimensions.PORTRAIT
            }

            return dimensions.SQUARE
        },
        async init () {

            const { params } = this.$route
            window.addEventListener('resize', this.evaluateViewport)
            this.enableKeyEscape()

            if (this.isModifying()) {
                this.bundleInstanceId = params.id
                await this.getBundleInstanceTiles(this.bundleInstanceId)
            } else {

                if (this.$route.query.bundleslug) {
                    this.updateBundleSlug(this.$route.query.bundleslug)
                }

                if (!this.bundleSlug) {
                    this.updateBundleSlug('best-selling-prints-bundle')
                }

                await this.getTiles(this.bundleSlug)
            }
            this.checkForMobile()

            await this.hideChat()
            this.$nextTick(() => {
                this.checkTileContainer()
            })
        },

        isModifying () {
            const { name } = this.$route

            return name === routes.ROUTE_MODIFY
        },

        evaluateViewport () {
            this.checkTileContainer()
            this.checkForMobile()
        },

        updateBundleSlug (bundleSlug) {
            this.setBundleSlug(bundleSlug)
        },

        checkTileContainer () {
            const tileWrapper = this.$refs.tiles
            const tileContainer = this.$refs.tileContainer

            this.tileContainerClassList = tileWrapper && tileContainer && (tileWrapper.offsetWidth > tileContainer.offsetWidth) ? 'justify-center' : ''
        },

        checkForMobile () {
            this.isMobile = !window.matchMedia('(min-width: 768px)').matches
        },

        async hideChat  () {
            window?.drift?.api?.widget?.hide?.()
        },

        handleClose () {
            this.$emit('close')
        },

        handleCloseMessages () {
            this.resetMessages()
        },

        handleShowImageSelector () {
            this.showImageSelector = true
        },

        handleShowConfirmQuit () {
            this.showConfirmQuit = true
        },

        addClasses () {
            document.body.classList.add('overflow-hidden', 'lg:overflow-hidden')
            document.body.classList.remove('lg:overflow-auto', 'lg:scrolling-touch')
        },

        removeClasses () {
            document.body.classList.add('lg:overflow-auto', 'lg:scrolling-touch')
            document.body.classList.remove('overflow-hidden', 'lg:overflow-hidden')
        },

        handlePaneClick (payload) {
            if (!this.isArt && !this.selectedEditToolId) {
                this.tileIndex = payload.tileIndex
                this.paneId = payload.paneId

                // save triggering tile to refocus upon closing tileBuilder
                if (this.selectedEditTileIndex === null) {
                    this.triggerTileElementId = document.activeElement.id
                }

                // open editor if image already selected
                const tileImages = this.tiles[this.tileIndex].productData.images
                const uploadLater = this.tiles[this.tileIndex].productData.uploadLater
                if (((tileImages && tileImages[this.paneId]) || uploadLater) && this.selectedEditTileIndex === null) {
                    this.toggleTileEditor(this.tileIndex)
                } else {
                    this.showImageSelector = true
                }
            }
        },

        async handleSelectImage (image) {
            const keys = Object.keys(this.tiles)
            const tile = this.tiles[keys[this.tileIndex]]

            this.updateTileOrientation(tile, image)
            await this.setTileImage({ tileIndex: this.tileIndex, paneId: this.paneId, image: image })
            this.toggleTileEditor(this.tileIndex)

            this.showImageSelector = false
        },

        updateTileOrientation (tile, image) {
            // get tile dimensions
            let tileWidth = parseInt(tile.productData.size.split('|')[0], 10)
            let tileHeight = parseInt(tile.productData.size.split('|')[1], 10)
            const tileAspectRatio = this.aspectRatio(tileWidth, tileHeight)

            // get image dimensions
            const imageAspectRatio = this.aspectRatio(image.width, image.height)

            if (tileAspectRatio !== dimensions.SQUARE && imageAspectRatio !== dimensions.SQUARE && imageAspectRatio !== tileAspectRatio) {
                tile.productData.size = tileHeight + '|' + tileWidth
            }
        },

        async handleAddTilesToCart () {
            this.renderMode = true
            await this.$nextTick()

            for (let i = 0; i < this.tiles.length; i++) {

                let el = this.$refs.tile[i].$el.querySelector('#printable')
                el = this.removePrintableTransform(el)

                this.tiles[i].svg = sanitizeSvg(serializeSvg(el))
                this.tiles[i].totalWidth = this.$refs.tile[i].totalWidth
                this.tiles[i].totalHeight = this.$refs.tile[i].totalHeight
            }
            if (this.isModifying()) {
                this.cartUpdateTiles({ bundleInstanceId: this.bundleInstanceId, tiles: this.tiles})
            } else {
                this.cartAddTiles(this.tiles)
            }
        },

        async handleUploadLater () {
            const keys = Object.keys(this.tiles)
            await this.unsetTileImage({ tileIndex: this.tileIndex, paneId: this.paneId })
            this.toggleTileEditor(this.tileIndex)
            this.showImageSelector = false
        },

        handleKeyEscape (event) {
            const key = 'which' in event ? event.which : event.keyCode
            if (key === 27) {
                if (!this.showImageSelector && !this.tileBuilderOpen) {
                    this.handleShowConfirmQuit()
                } else {
                    this.showImageSelector = false
                }
            }
        },

        translateString(key, dynamicString) {

            return translate(key, dynamicString, this, this.tiles.length)
        },

        focusTiles() {
            this.triggerElement = document.activeElement
            this.$nextTick(() => {
                // focus on the builder component on mount
                let builder = this.$refs.tiles
                builder?.focus()
            })
        },

        focusTriggerElement() {
            this.$nextTick(() => {
                // focus on the builder component on mount
                this.triggerElement?.focus()
            })
        },

        handleKeyDown (event) {
            if (this.trapFocusEnabled) {
                trapTabFocus(this.$refs.tiles, this.$refs.closeBtn.$el, event)
            }
        },

        removePrintableTransform (el) {
            if (el) el.style.transform = 'none'

            return el
        },

        getWrapperPaddingClasslist (productData) {

            let { productSlug, size } = productData

            let width = parseInt(size.split('|')[0], 10)
            let height = parseInt(size.split('|')[1], 10)
            let isFramedProduct
            switch(productSlug) {
                case products.PRODUCT_FRAMED_PRINT:
                case products.PRODUCT_ART_FRAMED_PRINT:
                case products.PRODUCT_COLLAGE_FRAMED_PRINT:

                    isFramedProduct = true
                    break

                default:

                    isFramedProduct = false
                    break
            }

            if ((height - 2) / height > 0.93) {

                return 'px-4 py-12'
            } else if (width > height && isFramedProduct) {

                return 'px-4 py-14'
            } else {

                return 'p-4'
            }
        },

        saveTileChanges () {
            this.toggleTileEditor(null)
            this.focustriggerTileElement()
        },

        focustriggerTileElement() {
            this.$nextTick(() => {
                document.getElementById(this.triggerTileElementId)?.focus()
            })
        },

        toggleTileEditor (tileIndex) {
            if (this.selectedEditTileIndex !== tileIndex) {
                this.selectedEditTileIndex = tileIndex
                this.tileBuilderOpen = tileIndex !== null;
            }
        },

        disableKeyEscape () {
            document.removeEventListener('keyup', this.handleKeyEscape)
        },

        enableKeyEscape () {
            document.addEventListener('keyup', this.handleKeyEscape)
        }
    },
}
</script>
